import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Search,
  Home,
  Palette,
  FolderOpen,
  Boxes,
  Images,
  BarChart3,
  Calendar,
  Settings as SettingsIcon,
  Sparkles,
  CornerDownLeft,
} from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Command palette (build-v2 G4) — ⌘K / Ctrl+K opens a centered overlay to jump
 * to any screen or run a quick action by keyboard, mirroring the prototype
 * `CmdKPalette`. Navigation + actions are filtered as you type; ↑/↓ move,
 * Enter runs, Esc closes.
 */
interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  to: string;
  keywords?: string;
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands = useMemo<Command[]>(
    () => [
      { id: 'home', label: 'Home', icon: <Home className="h-4 w-4" />, to: '/', keywords: 'inicio command center' },
      { id: 'brands', label: 'Brands', icon: <Palette className="h-4 w-4" />, to: '/brands', keywords: 'marcas studio' },
      { id: 'projects', label: 'Projects', icon: <FolderOpen className="h-4 w-4" />, to: '/projects', keywords: 'proyectos planner' },
      { id: 'assets', label: 'My Assets', icon: <Boxes className="h-4 w-4" />, to: '/assets', keywords: 'assets fotos purpose' },
      { id: 'photos', label: 'Photos', icon: <Images className="h-4 w-4" />, to: '/photos', keywords: 'fotos feed social' },
      { id: 'insights', label: 'Insights', icon: <BarChart3 className="h-4 w-4" />, to: '/insights', keywords: 'metricas ment recap' },
      { id: 'schedule', label: 'My Schedule', icon: <Calendar className="h-4 w-4" />, to: '/schedule', keywords: 'turnos shifts horario' },
      { id: 'settings', label: 'Settings', icon: <SettingsIcon className="h-4 w-4" />, to: '/settings', keywords: 'ajustes billing team' },
      { id: 'a-new-project', label: 'New project', hint: 'Action', icon: <Sparkles className="h-4 w-4" />, to: '/projects', keywords: 'crear nuevo proyecto' },
      { id: 'a-recap', label: 'Recap deck', hint: 'Action', icon: <Sparkles className="h-4 w-4" />, to: '/insights', keywords: 'recap deck slides export' },
      { id: 'a-pricing', label: 'Plans & pricing', hint: 'Action', icon: <Sparkles className="h-4 w-4" />, to: '/pricing', keywords: 'planes precios tiers' },
    ],
    [],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => `${c.label} ${c.keywords ?? ''}`.toLowerCase().includes(q));
  }, [commands, query]);

  // ⌘K / Ctrl+K toggles; Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);
  useEffect(() => {
    setActive(0);
  }, [query]);

  if (!open) return null;

  const run = (c: Command) => {
    setOpen(false);
    navigate(c.to);
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(filtered.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter' && filtered[active]) {
      e.preventDefault();
      run(filtered[active]);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center bg-black/60 backdrop-blur-sm pt-[12vh]"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-[var(--color-xk-border)] px-4 py-3">
          <Search className="h-4 w-4 flex-none text-[var(--color-xk-text-muted)]" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Ir a… o ejecutar una acción"
            className="flex-1 bg-transparent text-sm text-[var(--color-xk-lavender)] outline-none placeholder:text-[var(--color-xk-text-subtle)]"
          />
          <span className="mono rounded border border-[var(--color-xk-border)] px-1.5 py-0.5 text-[9px] text-[var(--color-xk-text-subtle)]">
            ESC
          </span>
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <div className="mono px-3 py-6 text-center text-xs text-[var(--color-xk-text-subtle)]">
              Sin resultados.
            </div>
          ) : (
            filtered.map((c, i) => (
              <button
                key={c.id}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => run(c)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  i === active
                    ? 'bg-[var(--color-accent-10)] text-[var(--color-xk-lavender)]'
                    : 'text-[var(--color-xk-text-muted)]',
                )}
              >
                <span className="text-[var(--color-xk-accent)]">{c.icon}</span>
                <span className="flex-1">{c.label}</span>
                {c.hint && (
                  <span className="mono rounded-full border border-[var(--color-xk-border)] px-2 py-0.5 text-[9px] text-[var(--color-xk-text-subtle)]">
                    {c.hint}
                  </span>
                )}
                {i === active && <CornerDownLeft className="h-3.5 w-3.5 text-[var(--color-xk-text-subtle)]" />}
              </button>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
